// ## context provider on the top level of PIM to manage the required data of the PIM
import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchMainProductDetails } from '../store/DPPModule.action';
import * as systemMasterAction from '../../../SystemMasters/Store/SystemMasters.action';
import * as colourAction from '../../../Library/ColorLibary/store/ColorLibrary.action';

import { generateColSizeLengthCombinations } from '../../../../Components/helper';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';

import { getProfileDetails } from '../../../UserSetting/store/User.action';

export const StateContext = createContext(null);

export default function DPPProductModuleStateProvider({ children }) {
  const reducerState = useSelector((redState) => redState);
  const {
    DPPProductReducer: { devProductData, factoriesData, dppProductDataById },
    SystemMastersDetails: { identifierType, productIdentifierList, lengthsList },
    UserDetails: { userInfo },
    Users: { profileDetails, logInRights },
    ColorLibraryDetails: { ColorWaysLibList },
  } = reducerState;

  const { CheckActionPermission } = useComponent();

  const dppAddEditAccess = CheckActionPermission(logInRights, 'dpp', 'all', 'addEdit');

  const dppDeleteAccess = CheckActionPermission(logInRights, 'dpp', 'all', 'delete');
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const { devOrderId } = useParams();

  const initialSetting = {
    cardList: [],
    selectedIdArray: [],
    archivedList: [],
    editPopup: false,
    editpopupData: null,
    editPopupItemIndex: -1,
    tableGridIndex: -1,
    tablePageIndex: -1,
    tableControls: null,
    disableDrag: false,
    modalEdit: false,
    controlPanelOpen: false,
    loading: false,
    selectedFactory: null,
    setFactoryData: [],
    navigateId: null,
    publishHtml: false,
    careExp: false,
    journeyExp: false,
    impactExp: false,
    dppPublishLoader: false,
  };

  const [DPPstate, setDPPState] = useState(initialSetting);

  const [allcombinationData, setAllCombinationData] = useState([]);

  const replaceAndFilterKeys = (obj, mapping, includeKeys = []) => {
    return Object.keys(obj).reduce((newObj, key) => {
      if (mapping[key] && includeKeys.includes(mapping[key])) {
        const newKey = mapping[key];
        newObj[newKey] = obj[key];
      }
      return newObj;
    }, {});
  };

  useEffect(() => {
    if (factoriesData && factoriesData?.length > 0) {
      const selectData = { value: factoriesData[0]?._id, label: factoriesData[0]?.factoryName };
      setDPPState((prev) => ({
        ...prev,
        selectedFactory: selectData,
      }));
    }
  }, [factoriesData]);

  useEffect(() => {
    if (DPPstate?.selectedFactory && factoriesData?.length) {
      const filterFactoryData = factoriesData.filter((data) => data?._id === DPPstate?.selectedFactory?.value);
      setDPPState((prev) => ({
        ...prev,
        setFactoryData: filterFactoryData,
      }));
    }
  }, [DPPstate?.selectedFactory, factoriesData]);

  useEffect(() => {
    const callData = async () => {
      let createCombination;
      if (!identifierType) {
        const sizeRanges = devProductData?.sizeRanges || [];
        const legLengths = lengthsList || [];
        const colourLibraries = ColorWaysLibList || [];
        createCombination = await generateColSizeLengthCombinations(sizeRanges, legLengths, colourLibraries);
        const sizeMasterId = productIdentifierList?.find((vl) => vl?.name === 'Size')?._id;
        const lengthMasterId = productIdentifierList?.find((vl) => vl?.name === 'Length')?._id;
        const colourMasterId = productIdentifierList?.find((vl) => vl?.name === 'Colourway')?._id;
        const keyMapping = {
          [colourMasterId]: 'colour',
          [sizeMasterId]: 'size',
          [lengthMasterId]: 'length',
        };

        let includeKeys = null;

        if (dppProductDataById?.colorwayToggel === true && dppProductDataById?.sizeLengthToggel === true) {
          includeKeys = ['colour', 'size', 'length'];
        } else if (dppProductDataById?.colorwayToggel === true && dppProductDataById?.sizeLengthToggel === false) {
          includeKeys = ['colour'];
        } else if (dppProductDataById?.colorwayToggel === false && dppProductDataById?.sizeLengthToggel === true) {
          includeKeys = ['size', 'length'];
        } else {
          includeKeys = [];
        }
        if (includeKeys.length > 0 && devProductData?.productIndentifier?.length > 1) {
          createCombination = devProductData.productIndentifier.map((item) => replaceAndFilterKeys(item, keyMapping, includeKeys));
        } else {
          createCombination = [];
        }
      } else {
        const sizeRanges = devProductData?.sizeRanges || [];
        const legLengths = devProductData?.legLengths || [];
        const colourLibraries = devProductData?.colourLibraries || [];
        if (dppProductDataById?.colorwayToggel || dppProductDataById?.sizeLengthToggel) {
          createCombination = await generateColSizeLengthCombinations(sizeRanges, legLengths, colourLibraries);
        }
      }
      setAllCombinationData(createCombination);
    };
    if (devProductData && Object.keys(devProductData).length > 0 && ColorWaysLibList && lengthsList && dppProductDataById) {
      callData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devProductData, productIdentifierList, ColorWaysLibList, lengthsList, dppProductDataById]);

  const fn = async () => {
    setDPPState((prev) => ({
      ...prev,
      setDPPState: true,
    }));
    const res = await dispatch(fetchMainProductDetails(devOrderId));
    await dispatch(systemMasterAction.fetchProductIdentifierType(t));
    await dispatch(systemMasterAction.fetchProductIdentifier(t));
    await dispatch(
      colourAction.fetchColorListLibrary({
        loggedInUserId: userInfo?.userId,
      }),
    );
    await dispatch(
      systemMasterAction.getLengthsDetails({
        isActive: true,
        loggedInUserId: userInfo?.userId,
      }),
    );
    await dispatch(getProfileDetails({ id: userInfo?.userId }));
    if (res) {
      setDPPState((prev) => ({
        ...prev,
        setDPPState: false,
      }));
    }
  };

  useEffect(() => {
    fn();
  }, [devOrderId]);

  const toggleEditPopup = () => {
    setDPPState((otherStates) => ({
      ...otherStates,
      editPopup: false,
      editpopupData: null,
      editPopupItemIndex: -1,
      editCustomSectionIndex: -1,
    }));
  };

  const contextValue = useMemo(() => {
    return {
      DPPstate,
      setDPPState,
      toggleEditPopup,
      allcombinationData,
      profileDetails,
      devOrderId,
      dppAddEditAccess,
      dppDeleteAccess,
      setAllCombinationData,
      userInfo,
    };
  }, [
    DPPstate,
    setDPPState,
    toggleEditPopup,
    allcombinationData,
    profileDetails,
    devOrderId,
    dppAddEditAccess,
    dppDeleteAccess,
    setAllCombinationData,
    userInfo,
  ]);

  return <StateContext.Provider value={contextValue}>{children}</StateContext.Provider>;
}

export function useDPProductModuleState() {
  const context = useContext(StateContext);
  if (!context) {
    throw new Error('useAppState must be used within the DAMModule State Provider');
  }
  return context;
}
